import React, { useState } from "react";
import { usePathname } from "next/navigation";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import ICMWrapper from "@/app/components/icm-wrapper";
import { getFaqsData, getFdFaqsData } from "@/app/api/service/homepage";
import { getProductSpecificFaqData } from "@/app/api/service/unlistedstocks";
import { useQuery } from "@tanstack/react-query";
import useMobile from "@/app/hooks/use-mobile";
import parse from "html-react-parser";

interface QuestionType {
    ques: string;
    ans: string;
}

interface SectionType {
    section: string;
    content: QuestionType[];
    isEditing: boolean;
}

interface FaqProps {
    bgColor: string;
    compact: boolean;
    productType?: string;
    product?: string;
}

const Faq: React.FC<FaqProps> = ({ bgColor, compact, productType, product }) => {
    const pathName = usePathname();
    const { isMobile } = useMobile();
    const [showAllSections, setShowAllSections] = useState(false);
    const [showAllQuestions, setShowAllQuestions] = useState(false);

    const { data: faqData, isFetched: isFaqFetched } = useQuery({
        queryKey: ["faqListData"],
        queryFn: () =>
            productType === "calculator" ? getFdFaqsData(product ?? "") : getFaqsData(),
    });

    const { data: productFaqData, isFetched: isProductFaqFetched } = useQuery({
        queryKey: ["productFaqListData", productType, product],
        queryFn: () => getProductSpecificFaqData(productType, product),
        enabled: !!productType && !!product && productType !== "calculator",
    });

    const handleToggleSections = () => {
        setShowAllSections((prev) => !prev);
    };

    const handleToggleQuestions = () => {
        setShowAllQuestions((prev) => !prev);
    };

    const generalSections = faqData?.data?.questions || [];
    const productSections = productFaqData?.data?.questions;
    const activeSections = productSections || generalSections;
    const displayedSections = showAllSections ? activeSections : activeSections.slice(0, 6);
    const showToggleButton = activeSections.length > 5;
    const isSingleSection = activeSections.length === 1;

    const renderQuestions = (questions: QuestionType[], isWithinSection: boolean = false) => {
        const shouldShowToggle = questions.length > 4 && isSingleSection && !isWithinSection;
        const displayedQuestions = isWithinSection
            ? questions
            : showAllQuestions
              ? questions
              : questions.slice(0, 4);

        return (
            <>
                <Accordion
                    id="section-child"
                    type="single"
                    collapsible
                    className={`mx-0 w-full md:mx-auto md:w-full ${bgColor}`}
                >
                    {displayedQuestions.map((item, index) => (
                        <AccordionItem
                            id={"section-child-item-" + (index+1)}
                            key={index}
                            value={`question-${index}`}
                            className={`w-full border-none ${bgColor}`}
                        >
                            <AccordionTrigger id={"section-child-q-" + (index+1)} className="!min-h-[58px] md:!min-h-[78px] w-full py-s7 text-left text-md md:text-lg font-semibold leading-[22.4px] md:leading-[29.4px] tracking-[-0.18px] text-neutral-900">
                                <span className="block pr-1 md:pr-0">{item.ques}</span>
                            </AccordionTrigger>
                            <AccordionContent id={"section-child-a-" + (index+1)} className="pb-s9 pr-s10 pt-s2 text-left text-sm font-regular leading-[22.4px] tracking-[0.14px] text-neutral-500 md:pr-s15 border-b border-neutralOpacityDark-20">
                                {parse(item.ans)}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                {shouldShowToggle && (
                    <div className="mt-4 flex items-center justify-center">
                        <RegularButton
                            id="btn-show-less-more"
                            noIcon
                            showRightIcon={false}
                            size={ButtonSize.Large}
                            variant={ButtonVariant.SecondaryDefault}
                            label={showAllQuestions ? "Show Less" : "Show More"}
                            onClick={handleToggleQuestions}
                            className="mt-8 w-full max-w-full md:w-auto"
                        />
                    </div>
                )}
            </>
        );
    };

    const renderSections = (sections: SectionType[]) => {
        if (sections.length === 1) {
            return renderQuestions(sections[0].content);
        }

        return (
            <Accordion
                id="section-main"
                type="single"
                collapsible
                className={`mx-0 w-full md:mx-auto md:max-w-[900px] ${bgColor} ${productType === "bullion" ? "md:!mx-0 md:!w-full md:!max-w-full" : ""}`}
            >
                {sections.map((section, index) => (
                    <AccordionItem
                        id={"section-main-item-" + (index+1)}
                        key={index}
                        value={`section-${index}`}
                        className={`${bgColor ? bgColor : "bg-white"} w-full !border-none px-0`}
                    >
                        <AccordionTrigger id={"section-main-q-" + (index+1)} className="!h-[58px] md:!min-h-[78px] border-b py-s7 border-neutralOpacityDark-20 text-left text-md md:text-lg font-medium leading-[22.4px] md:leading-[29.4px] tracking-[-0.18px] text-neutral-900">
                            <span className="block pr-1 md:pr-0">
                                {section.section}
                            </span>
                        </AccordionTrigger>
                        <AccordionContent id={"section-main-child-c" + (index+1)} className="p-0">
                            {renderQuestions(section.content, true)}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        );
    };

    const renderToggleButton = (id:string) => (
        <RegularButton
            id={id}
            noIcon
            showRightIcon={false}
            size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
            variant={ButtonVariant.SecondaryDefault}
            label={showAllSections ? "Show Less" : "Show More"}
            onClick={handleToggleSections}
            className="w-full max-w-full md:w-auto mt-8"
        />
    );

    if (
        !isFaqFetched ||
        (productType && product && !isProductFaqFetched && productType !== "calculator")
    ) {
        return null;
    }

    if (compact) {
        return (
            <div id="section-faq" className={bgColor}>
                <div
                    className={`mt-16 w-full md:mb-12 ${productType !== "calculator" ? "" : ""} mb-4`} 
                > {/* max-w-[812px] */}
                    <h2 id="section-title" className={`mb-6 ${productType === "calculator" ? 'text-xl md:text-4xl' : 'text-3xl'} font-semibold`}>FAQs</h2>
                    <div className="pb-s9 md:pb-s14">
                        {renderSections(displayedSections)}
                    </div>
                    {showToggleButton && !isSingleSection && (
                        <div className="flex items-center justify-center">
                            {renderToggleButton("section-btn-show-less-more")}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div id="section-faq" className={bgColor}>
            <ICMWrapper>
                <div className={`mx-auto pt-s11 pb-s15 text-center md:px-8 md:py-s20 ${productType === "bullion" ? "md:!px-0 md:-mx-6" : ""}`}>
                    <h2 id="section-title" className="mb-s9 text-4xl font-semibold tracking-[-0.48px] leading-[120%] md:text-6xl">
                        {pathName.includes("/unlisted-shares") ? "Unlisted Shares FAQs" : "FAQs"}
                    </h2>
                    <div className="md:pt-s9 md:px-8">
                        {renderSections(displayedSections)}
                    </div>
                    {showToggleButton && !isSingleSection && (
                        <div className="flex items-center justify-center">
                            {renderToggleButton("section-btn-show-less-more")}
                        </div>
                    )}
                </div>
            </ICMWrapper>
        </div>
    );
};

export default Faq;
